/**
 * Plans d'achat — les appels (V1.6-08).
 *
 * Trois règles tenues par ce module, et elles se ressemblent :
 *
 * 1. **aucun corps de requête ne porte d'argent.** Ni prix, ni transport, ni
 *    total. Un plan naît d'un nom de scénario, ou d'une liste `(besoin, offre,
 *    quantité)`. Tout le reste est calculé par le serveur, et c'est la seule
 *    façon d'empêcher qu'un chiffre affiché puis modifié dans un navigateur se
 *    retrouve approuvé ;
 * 2. **la péremption n'est jamais déduite ici.** L'écran ne compare pas les
 *    empreintes, ne redemande pas la comparaison pour voir si elle a bougé : il
 *    lit `staleness` et l'affiche. Le serveur reste seul juge, y compris au
 *    moment d'approuver ;
 * 3. **rien n'est acheté.** Il n'y a pas de bon de commande, pas d'envoi
 *    fournisseur, pas de réservation. `useApprovePlan` fige une décision, elle
 *    n'exécute rien.
 *
 * Comme la comparaison, un plan lu n'est pas gardé au frais : les soumissions
 * bougent pendant qu'un acheteur travaille, et une bannière « périmé » qui
 * arrive avec dix minutes de retard vaut moins que pas de bannière du tout.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "./client";
import type {
  PlanAllocationInput,
  PurchasePlanDetail,
  PurchasePlanPage,
  PurchasePlanStatus,
  ScenarioKind,
  Uuid,
} from "./types";

export const planKeys = {
  all: ["purchase-plans"] as const,
  list: (params: object) => ["purchase-plans", "list", params] as const,
  forRfq: (rfqId: Uuid) => ["purchase-plans", "rfq", rfqId] as const,
  one: (planId: Uuid) => ["purchase-plans", planId] as const,
  // `GET /purchase-plans/{id}/staleness` existe côté API pour un client qui
  // ne veut que la bannière. Aucun écran ne s'en sert : le détail rend déjà
  // la fraîcheur, et deux appels diraient la même chose. La clé reste pour
  // que les mutations l'invalident si un écran l'utilise un jour.
  staleness: (planId: Uuid) => ["purchase-plans", planId, "staleness"] as const,
};

export interface PurchasePlanParams {
  [key: string]: unknown;
  rfq_id?: Uuid;
  status?: PurchasePlanStatus;
  limit?: number;
  offset?: number;
}

function query(path: string, params?: Record<string, unknown>) {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params ?? {})) {
    if (value !== undefined && value !== null && value !== "") {
      search.set(key, String(value));
    }
  }
  const suffix = search.toString();
  return suffix ? `${path}?${suffix}` : path;
}

export function usePurchasePlans(params: PurchasePlanParams = {}) {
  return useQuery({
    queryKey: planKeys.list(params),
    queryFn: () => apiFetch<PurchasePlanPage>(query("/purchase-plans", params)),
  });
}

export function useRfqPurchasePlans(rfqId: Uuid | undefined) {
  return useQuery({
    queryKey: planKeys.forRfq(rfqId ?? ""),
    queryFn: () => apiFetch<PurchasePlanPage>(`/rfqs/${rfqId}/purchase-plans`),
    enabled: Boolean(rfqId),
  });
}

/**
 * Le plan entier, **avec sa fraîcheur telle que le serveur la voit**.
 *
 * `staleTime: 0` est délibéré : ce qui rend un plan périmé se passe ailleurs
 * (un fournisseur révise, un prix change), donc rien dans cet écran ne peut
 * prévenir le cache qu'il ment.
 */
export function usePurchasePlan(planId: Uuid | undefined) {
  return useQuery({
    queryKey: planKeys.one(planId ?? ""),
    queryFn: () => apiFetch<PurchasePlanDetail>(`/purchase-plans/${planId}`),
    enabled: Boolean(planId),
    staleTime: 0,
  });
}

/**
 * Créer un brouillon : soit depuis un scénario, soit depuis des allocations.
 *
 * Les deux chemins existent parce que les scénarios ne rendent que des
 * couvertures complètes. Quand aucun fournisseur ne peut tout fournir, aucun
 * scénario ne conclut — et acheter ce qui est disponible reste une décision
 * qu'il faut pouvoir écrire.
 */
export interface CreatePlanInput {
  rfqId: Uuid;
  scenario?: ScenarioKind;
  allocations?: PlanAllocationInput[];
  notes?: string | null;
}

export function useCreatePurchasePlan() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ rfqId, scenario, allocations, notes }: CreatePlanInput) =>
      apiFetch<PurchasePlanDetail>(`/rfqs/${rfqId}/purchase-plans`, {
        method: "POST",
        body: {
          ...(scenario ? { scenario } : { allocations }),
          notes: notes || null,
        },
      }),
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: planKeys.all });
      queryClient.setQueryData(planKeys.one(data.plan.id), data);
    },
  });
}

function usePlanMutation<TInput extends { planId: Uuid }>(
  path: (input: TInput) => string,
  method: "POST" | "PATCH",
  body?: (input: TInput) => unknown,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: TInput) =>
      apiFetch<PurchasePlanDetail>(path(input), {
        method,
        body: body ? body(input) : undefined,
      }),
    onSuccess: (data, variables) => {
      queryClient.setQueryData(planKeys.one(variables.planId), data);
      void queryClient.invalidateQueries({ queryKey: planKeys.staleness(variables.planId) });
      void queryClient.invalidateQueries({ queryKey: planKeys.list({}) });
      void queryClient.invalidateQueries({ queryKey: planKeys.forRfq(data.plan.rfq_id) });
      // Une approbation peut avoir remplacé une révision précédente : sa fiche
      // à elle aussi a changé de statut.
      void queryClient.invalidateQueries({ queryKey: planKeys.all });
    },
  });
}

/** Remplacer le contenu d'un brouillon. **Aucun montant dans le corps.** */
export function useUpdatePurchasePlan() {
  return usePlanMutation<{
    planId: Uuid;
    allocations: PlanAllocationInput[];
    notes?: string | null;
  }>(
    ({ planId }) => `/purchase-plans/${planId}`,
    "PATCH",
    ({ allocations, notes }) => ({ allocations, notes: notes ?? null }),
  );
}

/**
 * Reconstruire le plan sur les données d'aujourd'hui.
 *
 * Un brouillon est reconstruit en place ; un plan approuvé ne bouge pas, et
 * une nouvelle révision en brouillon apparaît à côté. C'est le serveur qui
 * décide lequel des deux, pas l'écran.
 */
export function useRefreshPurchasePlan() {
  return usePlanMutation<{ planId: Uuid }>(
    ({ planId }) => `/purchase-plans/${planId}/refresh`,
    "POST",
  );
}

/**
 * Approuver, en nommant un par un les avertissements acceptés.
 *
 * Il n'y a volontairement pas de `force`. Un plan périmé est refusé par le
 * serveur avec `STALE_PLAN` ; la réponse de l'écran est de rafraîchir et de
 * relire, pas de passer outre.
 */
export function useApprovePurchasePlan() {
  return usePlanMutation<{ planId: Uuid; acknowledgements?: string[] }>(
    ({ planId }) => `/purchase-plans/${planId}/approve`,
    "POST",
    ({ acknowledgements }) => ({ acknowledgements: acknowledgements ?? [] }),
  );
}

export function useCancelPurchasePlan() {
  return usePlanMutation<{ planId: Uuid; reason: string }>(
    ({ planId }) => `/purchase-plans/${planId}/cancel`,
    "POST",
    ({ reason }) => ({ reason }),
  );
}
